import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { GenderEnum, genderOneLetterMapping } from '../../../../lib/types/enums/common/gender.enum'
import { EnrollmentSiblingInput } from '../../../../lib/types/models/enrollment-siblings/enrollment-siblings-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../../lib/utils/functions/misc.functions'
import { createEnumSchema } from '../../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'

type Props = BaseFormProps<EnrollmentSiblingInput, true> & {
  className?: string
}

export const formSchema: ZodType<EnrollmentSiblingInput> = z.object({
  name: createStringSchema('nom'),
  age: createNumberSchema('âge', { isInteger: true }),
  gender: createEnumSchema({ fieldName: 'sexe', enumType: GenderEnum })
})

const EnrollmentSiblingsForm = ({ className, defaultValues, ...validationButtonProps }: Props) => {
  const form = useForm<EnrollmentSiblingInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-3', className)}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required type="text" label="Prénom" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="gender"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    label="Sexe"
                    required
                    options={Object.values(GenderEnum).map((gender) => ({
                      label: genderOneLetterMapping[gender].label,
                      value: gender
                    }))}
                    selectedOption={getSelectedEnumOption(field.value, genderOneLetterMapping)}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="age"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput
                    {...field}
                    required
                    type="number"
                    allowNegative={false}
                    label="Âge"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default EnrollmentSiblingsForm
